import React, { useEffect } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { getRoleDetails } from "store/actions/rolesAction"
import { Card, CardBody, CardTitle, Col, Row, Table } from "reactstrap"
import Spinners from "components/Shared/Spinners"
import { useTranslation } from "react-i18next"
import isPermissionEnabled from "common/isPermissionEnabled"
import { ROUTES } from "constants/routes"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"

const SuperAdminRolesDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Role Details | Super Admin")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const { loading, roleDetails } = useSelector(state => state.Roles)
  const { userRole, accountType, accountScope } = useSelector(state => state.Login)

  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"
  const canViewRoles = isPermissionEnabled(
    userRole,
    102,
    "sa_roles_1",
    accountType,
    accountScope
  )

  useEffect(() => {
    if (!isSuperAdminSession || !canViewRoles) {
      navigateToAccessibleRoute(navigate)
      return
    }
    dispatch(getRoleDetails({ id }))
  }, [isSuperAdminSession, canViewRoles, id, dispatch, navigate])

  if (loading) return <Spinners />

  return (
    <div className="page-content  customers_page">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col>
            <Card>
              <CardBody>
                <CardTitle className="d-flex align-items-center">
                  {t("Role Details")}
                </CardTitle>
                <div className="table-responsive">
                  {(roleDetails && Object.values(roleDetails)?.length === 0) ||
                  roleDetails == null ? (
                    <p className="text-center">{t("No role details found")}</p>
                  ) : (
                    <Table className="mb-0">
                      <tbody>
                        <tr>
                          <th scope="row">{t("Id")}</th>
                          <td>{roleDetails._id}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Name(English)")}</th>
                          <td>{roleDetails.name || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Name(عربي)")}</th>
                          <td>{roleDetails.nameAr || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Description(English)")}</th>
                          <td>{roleDetails.description || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Description(عربي)")}</th>
                          <td>{roleDetails.descriptionAr || "-"}</td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col xl={8}>
            <Card>
              <CardBody>
                <CardTitle>{t("Role Permissions")}</CardTitle>
                <Row>
                  {roleDetails?.permissions?.map((permissionGroup, index) => {
                    const filteredPermissions =
                      permissionGroup.permissions.filter(permission => permission.isEnabled)
                    if (filteredPermissions.length === 0) return null

                    return (
                      <Col xl={6} ms={6} key={index}>
                        <h4 className="font-size-14 my-4">
                          {language === "sa"
                            ? permissionGroup.titleAr
                            : permissionGroup.title}
                        </h4>
                        <ul className="list-group d-flex flex-column">
                          {filteredPermissions.map((permission, subIndex) => (
                            <li
                              className="list-group-item not_allowed_cursor"
                              key={subIndex}
                            >
                              <div className="form-check not_allowed_cursor">
                                <input
                                  className="form-check-input not_allowed_cursor"
                                  id={permission.id}
                                  type="checkbox"
                                  name={permission.id}
                                  checked={permission.isEnabled}
                                  readOnly
                                />
                                <label className="form-check-label not_allowed_cursor">
                                  {language === "sa"
                                    ? permission.labelAr
                                    : permission.label}
                                </label>

                                {permission.subPermissions && (
                                  <ul className="list-group d-flex flex-column mt-3">
                                    {permission.subPermissions.map(
                                      (subPermission, subPermissionIndex) => (
                                        <li
                                          className="list-group-item not_allowed_cursor"
                                          key={subPermissionIndex}
                                        >
                                          <div className="form-check not_allowed_cursor">
                                            <input
                                              className="form-check-input not_allowed_cursor"
                                              id={subPermission.id}
                                              type="checkbox"
                                              name={subPermission.id}
                                              checked={subPermission.isEnabled}
                                              readOnly
                                            />
                                            <label className="form-check-label not_allowed_cursor">
                                              {language === "sa"
                                                ? subPermission.labelAr
                                                : subPermission.label}
                                            </label>
                                          </div>
                                        </li>
                                      )
                                    )}
                                  </ul>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </Col>
                    )
                  })}
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col xl={4}>
            <Card>
              <CardBody>
                <CardTitle>{t("Assigned Users")}</CardTitle>
                <div className="table-responsive" style={{ maxHeight: 890 }}>
                  {roleDetails?.users?.length === 0 ? (
                    <p className="text-center">{t("No assigned users found")}</p>
                  ) : (
                    <Table className="table-nowrap mb-0" hover>
                      <thead className="table-light">
                        <tr>
                          <th>{t("S. No.")}</th>
                          <th>{t("Name")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roleDetails?.users?.map((user, index) => (
                          <tr
                            key={user._id}
                            onClick={() =>
                              navigate(`/superadmin/users/user-details?id=${user._id}`)
                            }
                            className="cursor-pointer"
                          >
                            <td>{index + 1}</td>
                            <td>
                              <span className="text-capitalize">
                                {user.firstName} {user.lastName}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default SuperAdminRolesDetails
